import { UUID } from 'node:crypto';
import { Injectable } from '@angular/core';
import { ITeamProfile } from '@shared/services/teamProfiles.service';
import { IWorktask, WorktaskService } from '@shared/services/worktask.service';
import { BehaviorSubject, catchError, of, Subject } from 'rxjs';
import { IShiftModel, MesShiftService } from '../mes-shift.service';
import { IEmployeeItemsModel, IShiftCreationResult } from './create-shift-note.models';
import { getActiveConfiguration, getWorkDayForDate } from './team-profile.helpers';

export interface IShiftCreationRequest {
	employees: IEmployeeItemsModel[];
	startDate: Date;
	endDate: Date;
	startTime: string;
	endTime: string;
	workTask: IWorktask;
	useTeamProfile: boolean;
	teamProfiles: ITeamProfile[];
}

@Injectable({
	providedIn: 'root',
})
export class ShiftCreationOrchestratorService {
	private progressSubject = new BehaviorSubject<{ completed: number; total: number }>({
		completed: 0,
		total: 0,
	});
	private cancelledSubject = new Subject<void>();
	private cancelled = false;

	progress$ = this.progressSubject.asObservable();

	constructor(private shiftService: MesShiftService) {}

	cancel(): void {
		this.cancelled = true;
		this.cancelledSubject.next();
	}

	async executeCreate(request: IShiftCreationRequest): Promise<IShiftCreationResult[]> {
		this.cancelled = false;
		const results: IShiftCreationResult[] = [];
		const dates = this.getDateRange(request.startDate, request.endDate);
		const total = request.employees.length * dates.length;
		let completed = 0;

		this.progressSubject.next({ completed: 0, total });

		for (const employee of request.employees) {
			for (const date of dates) {
				if (this.cancelled) {
					results.push({
						employeeName: employee.text,
						date,
						status: 'cancelled',
						reason: 'cancelled-by-user',
					});
					completed++;
					this.progressSubject.next({ completed, total });
					continue;
				}

				if (request.useTeamProfile) {
					const teamProfile = request.teamProfiles.find((tp) => tp.id === employee.teamProfileId);
					if (!teamProfile) {
						results.push({
							employeeName: employee.text,
							date,
							status: 'skipped',
							reason: 'no-team-profile',
						});
						completed++;
						this.progressSubject.next({ completed, total });
						continue;
					}

					const activeConfig = getActiveConfiguration(teamProfile.configurations, date);
					if (!activeConfig) {
						results.push({
							employeeName: employee.text,
							date,
							status: 'skipped',
							reason: 'no-active-configuration',
						});
						completed++;
						this.progressSubject.next({ completed, total });
						continue;
					}

					const workDay = getWorkDayForDate(activeConfig.weekDays, date);
					if (!workDay.isWorkDay) {
						results.push({
							employeeName: employee.text,
							date,
							status: 'skipped',
							reason: 'not-a-work-day',
						});
						completed++;
						this.progressSubject.next({ completed, total });
						continue;
					}

					if (workDay.workTime.startTime === workDay.workTime.endTime) {
						results.push({
							employeeName: employee.text,
							date,
							status: 'skipped',
							reason: 'zero-length-work-day',
						});
						completed++;
						this.progressSubject.next({ completed, total });
						continue;
					}

					// Use team profile times
					const startTime = workDay.workTime.startTime;
					const endTime = workDay.workTime.endTime;
					const result = await this.createShift(employee, date, startTime, endTime, request.workTask);
					results.push(result);
				} else {
					const result = await this.createShift(employee, date, request.startTime, request.endTime, request.workTask);
					results.push(result);
				}

				completed++;
				this.progressSubject.next({ completed, total });

				// Small delay between requests to avoid overloading the API
				if (completed < total) {
					await this.delay(100);
				}
			}
		}

		return results;
	}

	private async createShift(employee: IEmployeeItemsModel, date: Date, startTime: string, endTime: string, workTask: IWorktask): Promise<IShiftCreationResult> {
		const [startHours, startMinutes] = startTime.split(':').map(Number);
		const [endHours, endMinutes] = endTime.split(':').map(Number);

		const startDate = new Date(date);
		startDate.setHours(startHours, startMinutes, 0, 0);

		const endDate = new Date(date);
		endDate.setHours(endHours, endMinutes, 0, 0);

		// A work day that crosses midnight belongs to the day it starts, so the end moves forward.
		if (startHours > endHours || (startHours === endHours && startMinutes > endMinutes)) {
			endDate.setDate(endDate.getDate() + 1);
		}

		const newShift: IShiftModel = {
			employeeId: employee.employeeId as UUID,
			rawStartTime: startDate,
			rawEndTime: endDate,
			approved: false,
			validationErrors: null,
			timestamps: [
				{ workTaskId: workTask.id as UUID, dateTimeOffset: startDate.toISOString() },
				{
					workTaskId: WorktaskService.offWorkTaskId as UUID,
					dateTimeOffset: endDate.toISOString(),
				},
			],
		};

		return new Promise<IShiftCreationResult>((resolve) => {
			this.shiftService
				.createShift(newShift)
				.pipe(
					catchError((error) => {
						return of({ error });
					}),
				)
				.subscribe((response: any) => {
					if (response.error) {
						const status = response.error?.status;
						if (status === 409) {
							resolve({
								employeeName: employee.text,
								date,
								status: 'failed',
								reason: 'shift-already-exists',
							});
						} else {
							resolve({
								employeeName: employee.text,
								date,
								status: 'failed',
								reason: 'api-error',
								reasonDetail: response.error?.message || '',
							});
						}
					} else {
						resolve({
							employeeName: employee.text,
							date,
							status: 'success',
						});
					}
				});
		});
	}

	private getDateRange(startDate: Date, endDate: Date): Date[] {
		const dates: Date[] = [];
		const current = new Date(startDate);
		current.setHours(12, 0, 0, 0);
		const end = new Date(endDate);
		end.setHours(12, 0, 0, 0);

		while (current.getTime() <= end.getTime()) {
			dates.push(new Date(current));
			current.setDate(current.getDate() + 1);
		}
		return dates;
	}

	private async delay(ms: number): Promise<void> {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}
}
